import { Controller } from "@hotwired/stimulus"

/**
 * Connection Monitor Controller
 *
 * Monitors ActionCable WebSocket connection health and shows indicator when disconnected.
 * Uses ActionCable's built-in connection state rather than application-level events.
 *
 * ActionCable's internal monitor already handles:
 * - Server heartbeat pings every 3 seconds
 * - Automatic reconnection on connection loss
 * - Visibility change handling
 *
 * This controller simply reflects the connection state in the UI.
 *
 * @example Basic usage
 *   <div data-controller="connection-monitor">
 *     <div data-connection-monitor-target="indicator" class="d-none">
 *       Connection interrupted - reconnecting...
 *     </div>
 *   </div>
 *
 * @example With recovery options
 *   <div data-controller="connection-monitor"
 *        data-connection-monitor-allow-full-reload-value="true"
 *        data-connection-monitor-frame-id-value="my-turbo-frame">
 *   </div>
 */
export default class extends Controller {
  static values = {
    // Optional: specific Turbo Frame ID to refresh on reconnection
    frameId: String,
    // Whether to use full page reload as fallback (default: false - let ActionCable handle it)
    allowFullReload: { type: Boolean, default: false },
    // Delay before showing indicator (debounce brief disconnections)
    showDelay: { type: Number, default: 2000 },
    // Time in ms before showing "refresh page" message (default: 1 minute)
    permanentErrorTimeout: { type: Number, default: 60000 }
  }

  static targets = ["indicator"]

  connect() {
    this.isDisconnected = false
    this.isPermanentError = false
    this.showTimeout = null
    this.permanentErrorTimeout = null
    this.disconnectedAt = null

    this.attachConnectionListeners()
    this.attachVisibilityListener()

    // Check initial connection state
    this.checkConnectionState()

    // Expose controller for testing (development only)
    if (process.env.NODE_ENV !== 'production') {
      console.log('[ConnectionMonitor] Connected, monitoring ActionCable state')
      this.element._connectionMonitorController = this
    }
  }

  disconnect() {
    this.detachConnectionListeners()
    this.detachVisibilityListener()
    this.clearShowTimeout()
    this.clearPermanentErrorTimeout()

    if (this.element._connectionMonitorController) {
      delete this.element._connectionMonitorController
    }
  }

  // --- ActionCable Connection State Monitoring ---

  attachConnectionListeners() {
    // Get the ActionCable consumer
    this.consumer = window.__acConsumer

    if (!this.consumer) {
      console.warn('[ConnectionMonitor] ActionCable consumer not found')
      return
    }

    // Monitor WebSocket events directly
    const connection = this.consumer.connection

    // Store original handlers so we can chain
    this.originalOnOpen = connection.webSocket?.onopen
    this.originalOnClose = connection.webSocket?.onclose
    this.originalOnError = connection.webSocket?.onerror

    // Hook into connection events using a polling approach
    // (ActionCable doesn't expose clean event hooks, so we poll the state)
    this.boundCheckState = this.checkConnectionState.bind(this)
    this.stateCheckInterval = setInterval(this.boundCheckState, 1000)

    // Also listen for Turbo Stream events as a positive signal
    this.boundRecordActivity = this.recordActivity.bind(this)
    document.addEventListener('turbo:before-stream-render', this.boundRecordActivity)
    this.element.addEventListener('cable:received', this.boundRecordActivity)
  }

  detachConnectionListeners() {
    if (this.stateCheckInterval) {
      clearInterval(this.stateCheckInterval)
      this.stateCheckInterval = null
    }

    if (this.boundRecordActivity) {
      document.removeEventListener('turbo:before-stream-render', this.boundRecordActivity)
      this.element.removeEventListener('cable:received', this.boundRecordActivity)
    }
  }

  /**
   * Check ActionCable's actual connection state.
   * Shows indicator only when WebSocket is truly disconnected.
   */
  checkConnectionState() {
    if (!this.consumer) return

    const connection = this.consumer.connection
    const isOpen = connection.isOpen()

    if (!isOpen && !this.isDisconnected) {
      // Connection lost - show indicator after delay (debounce brief disconnections)
      this.scheduleShowIndicator()
    } else if (isOpen && this.isDisconnected) {
      // Connection restored
      this.handleReconnected()
    }
  }

  scheduleShowIndicator() {
    if (this.showTimeout) return // Already scheduled

    this.showTimeout = setTimeout(() => {
      // Re-check state after delay
      if (!this.consumer?.connection.isOpen()) {
        this.handleDisconnected()
      }
      this.showTimeout = null
    }, this.showDelayValue)
  }

  clearShowTimeout() {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout)
      this.showTimeout = null
    }
  }

  handleDisconnected() {
    if (this.isDisconnected) return

    this.isDisconnected = true
    this.disconnectedAt = Date.now()
    this.showStaleIndicator()

    // Schedule escalation to permanent error state
    this.schedulePermanentError()

    console.log('[ConnectionMonitor] WebSocket disconnected')
  }

  handleReconnected() {
    this.clearShowTimeout()
    this.clearPermanentErrorTimeout()
    this.isDisconnected = false
    this.disconnectedAt = null

    // If we were in permanent error state, restore original indicator content
    if (this.isPermanentError) {
      this.restoreOriginalIndicator()
    }
    this.isPermanentError = false

    this.hideStaleIndicator()

    console.log('[ConnectionMonitor] WebSocket reconnected')
  }

  // --- Permanent Error Escalation ---

  schedulePermanentError() {
    if (this.permanentErrorTimeoutId) return // Already scheduled

    this.permanentErrorTimeoutId = setTimeout(() => {
      if (this.isDisconnected && !this.isPermanentError) {
        this.showPermanentError()
      }
    }, this.permanentErrorTimeoutValue)
  }

  clearPermanentErrorTimeout() {
    if (this.permanentErrorTimeoutId) {
      clearTimeout(this.permanentErrorTimeoutId)
      this.permanentErrorTimeoutId = null
    }
  }

  showPermanentError() {
    this.isPermanentError = true
    console.log('[ConnectionMonitor] Escalating to permanent error state')

    // Update indicator to show "refresh page" message
    this.showPermanentStaleState()
  }

  /**
   * Records activity from application events.
   * If we receive data, the connection is definitely working.
   */
  recordActivity() {
    if (this.isDisconnected) {
      this.handleReconnected()
    }
    this.clearShowTimeout()
  }

  // --- Visibility Handling ---

  attachVisibilityListener() {
    this.boundVisibilityChange = this.handleVisibilityChange.bind(this)
    document.addEventListener('visibilitychange', this.boundVisibilityChange)
  }

  detachVisibilityListener() {
    if (this.boundVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundVisibilityChange)
    }
  }

  handleVisibilityChange() {
    if (!document.hidden) {
      // Tab became visible - check connection state
      this.checkConnectionState()
    }
  }

  /**
   * Force disconnection state for testing.
   * Only available in non-production environments.
   */
  simulateDisconnect() {
    if (process.env.NODE_ENV === 'production') {
      console.warn('[ConnectionMonitor] simulateDisconnect is disabled in production')
      return
    }

    this.handleDisconnected()
  }

  simulateReconnect() {
    if (process.env.NODE_ENV === 'production') {
      console.warn('[ConnectionMonitor] simulateReconnect is disabled in production')
      return
    }

    this.handleReconnected()
  }

  // --- UI Indicators (Safe DOM methods - no innerHTML) ---

  showStaleIndicator() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.classList.remove('d-none')
      this.indicatorTarget.classList.add('connection-stale-indicator')
    }
    this.element.classList.add('connection-stale')
    this.element.dataset.connectionStale = 'true'
  }

  hideStaleIndicator() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.classList.add('d-none')
    }
    this.element.classList.remove('connection-stale')
    delete this.element.dataset.connectionStale
  }

  updateIndicatorStatus(message) {
    if (this.hasIndicatorTarget) {
      const textEl = this.indicatorTarget.querySelector('[data-connection-text]')
      if (textEl) {
        textEl.textContent = message
      }
    }
  }

  /**
   * Shows permanent stale state with refresh link.
   * Uses safe DOM methods instead of innerHTML to prevent XSS.
   */
  showPermanentStaleState() {
    if (!this.hasIndicatorTarget) return

    // Ensure indicator stays visible
    this.indicatorTarget.classList.remove('d-none')
    this.indicatorTarget.classList.add('connection-stale-indicator')

    // Clear existing content safely
    while (this.indicatorTarget.firstChild) {
      this.indicatorTarget.removeChild(this.indicatorTarget.firstChild)
    }

    // Build DOM elements safely (matching original structure for consistent styling)
    const icon = document.createElement('i')
    icon.className = 'fas fa-exclamation-triangle'
    this.indicatorTarget.appendChild(icon)

    // Add space after icon (CSS margin doesn't apply after FontAwesome converts <i> to <svg>)
    this.indicatorTarget.appendChild(document.createTextNode(' '))

    const text = document.createElement('span')
    text.textContent = 'Connection lost. '
    this.indicatorTarget.appendChild(text)

    const link = document.createElement('a')
    link.href = window.location.href
    link.className = 'alert-link text-decoration-underline'
    link.textContent = 'Refresh page'
    link.addEventListener('click', (e) => {
      e.preventDefault()
      window.location.reload()
    })
    this.indicatorTarget.appendChild(link)
  }

  /**
   * Restores the original indicator content after permanent error state.
   * Called when connection recovers so next disconnect shows initial message.
   */
  restoreOriginalIndicator() {
    if (!this.hasIndicatorTarget) return

    // Clear the permanent error content
    while (this.indicatorTarget.firstChild) {
      this.indicatorTarget.removeChild(this.indicatorTarget.firstChild)
    }

    // Rebuild original content
    const icon = document.createElement('i')
    icon.className = 'fas fa-exclamation-triangle'
    this.indicatorTarget.appendChild(icon)

    // Add space after icon (CSS margin doesn't apply after FontAwesome converts <i> to <svg>)
    this.indicatorTarget.appendChild(document.createTextNode(' '))

    const text = document.createElement('span')
    text.setAttribute('data-connection-text', '')
    text.textContent = 'Connection interrupted - reconnecting...'
    this.indicatorTarget.appendChild(text)
  }
}
